import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import GoTo from "@/app/shared/GoTo";
import Avatar from "@/design/components/Avatar";
import Search from "@/design/components/Search";
import InputPhone from "@/design/forms/InputPhone";
import { showSnackbar } from "@/store/reducers/app";
import useGoTo from "@/utils/hooks/useGoTo";
import useSearch from "@/utils/hooks/useSearch";
import Valkyrie, {
  viArrowLeft,
  viCopy,
  viEarth,
  viFloppyDisk,
  viList,
  viLocationPinDot,
  viMegaphone,
  viMusic,
  viNumpad,
  viPeople,
  viPeopleCircle,
  viPerson,
  viRectangleP,
  viSmartphone,
  viTableClock,
  viToggleOn,
  viTrashCan,
  viVoicemail,
  viXmark,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Divider,
  Drawer,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

export default function OverflowDrawer({
  value,
  label,
  required,
  propertyName,
  overflowHandler,
  helperText,
  options = {},
  filter = { exten: [], custom: [], sound: [] },
  error,
}) {
  const [drawer, setDrawer] = useState(false);
  const [unrestricted, setUnrestricted] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const { getGoTo } = useGoTo();
  const dispatch = useDispatch();

  const currentOverflow = useMemo(() => getGoTo(value), [getGoTo, value]);
  const [overflowType, setOverflowType] = useState(null);

  const customDestinationStore = useSelector((state) => state.domain.customDestinations);
  const groupStore = useSelector((state) => state.domain.groups);
  const ivrStore = useSelector((state) => state.domain.ivrs);
  const queueStore = useSelector((state) => state.domain.queues);
  const soundStore = useSelector((state) => state.domain.sounds);
  const timetableStore = useSelector((state) => state.domain.timetables);
  const userStore = useSelector((state) => state.domain.users);
  const mobileStore = useSelector((state) => state.domain.mobile);
  const callParkingStore = useSelector((state) => state.domain.callParkings);
  const voicemailStore = useSelector((state) => state.domain.voicemails);
  const conferenceStore = useSelector((state) => state.domain.conferences);
  const intercomStore = useSelector((state) => state.domain.intercoms);
  const dayNightStore = useSelector((state) => state.domain.dayNights);

  const unrestrictedValue = unrestricted ?? value?.custom ?? "";

  const applyValue = (newValue) => {
    if (newValue) {
      overflowHandler({ [overflowType === "1" ? "custom" : overflowType === "5" ? "sound" : "exten"]: newValue });
    } else {
      overflowHandler(null);
    }
    setDrawer(false);
    setUnrestricted(null);
    setOverflowType(null);
    setSearchValue("");
  };

  const isEnabled = useCallback((type) => !Object.hasOwn(options, type) || options[type], [options]);

  const getSelects = useCallback(
    (defaultType = undefined) => {
      const switchType = defaultType || overflowType;

      switch (switchType) {
        case "2":
          return userStore.list.filter((user) => filter.exten.indexOf(user.exten) === -1);
        case "3":
          return groupStore.list.filter((group) => filter.exten.indexOf(group.exten) === -1);
        case "4":
          return timetableStore.list.filter((timetable) => filter.exten.indexOf(timetable.exten) === -1);
        case "5":
          return soundStore.list.filter((sound) => filter.sound.indexOf(sound.exten) === -1);
        case "6":
          return voicemailStore.list.filter((voicemail) => filter.exten.indexOf(voicemail.exten) === -1);
        case "7":
          return queueStore.list.filter((queue) => filter.exten.indexOf(queue.exten) === -1);
        case "8":
          return ivrStore.list.filter((ivr) => filter.exten.indexOf(ivr.exten) === -1);
        case "11":
          return customDestinationStore.list.filter((destination) => filter.exten.indexOf(destination.exten) === -1);
        case "12":
          return conferenceStore.list.filter((conference) => filter.exten.indexOf(conference.exten) === -1);
        case "13":
          return callParkingStore.list.filter((callParking) => filter.exten.indexOf(callParking.exten) === -1);
        case "14":
          return intercomStore.list.filter((intercom) => filter.exten.indexOf(intercom.exten) === -1);
        case "15":
          return dayNightStore.list.filter((dayNight) => filter.exten.indexOf(dayNight.exten) === -1);
        case "17":
          return mobileStore.list.filter((mobile) => filter.exten.indexOf(mobile.exten) === -1);
        default:
          return [];
      }
    },
    [
      overflowType,
      userStore.list,
      groupStore.list,
      timetableStore.list,
      soundStore.list,
      voicemailStore.list,
      queueStore.list,
      ivrStore.list,
      customDestinationStore.list,
      conferenceStore.list,
      callParkingStore.list,
      intercomStore.list,
      dayNightStore.list,
      mobileStore.list,
      filter.exten,
      filter.sound,
    ],
  );

  const getOptions = useCallback(() => {
    const options = [];

    if (isEnabled("users"))
      options.push({
        value: "2",
        title: "User",
        disabled: getSelects("2").length === 0,
        icon: viPerson,
      });
    if (isEnabled("mobile"))
      options.push({
        value: "17",
        title: "Mobile user",
        disabled: getSelects("17").length === 0,
        icon: viSmartphone,
      });
    if (isEnabled("groups"))
      options.push({
        value: "3",
        title: "Group",
        disabled: getSelects("3").length === 0,
        icon: viPeople,
      });
    if (isEnabled("timetables"))
      options.push({
        value: "4",
        title: "Timetable",
        disabled: getSelects("4").length === 0,
        icon: viTableClock,
      });
    if (isEnabled("sounds"))
      options.push({
        value: "5",
        title: "Sound",
        disabled: getSelects("5").length === 0,
        icon: viMusic,
      });
    if (isEnabled("ivrs"))
      options.push({
        value: "8",
        title: "IVR",
        disabled: getSelects("8").length === 0,
        icon: viNumpad,
      });
    if (isEnabled("queues"))
      options.push({
        value: "7",
        title: "Queue",
        disabled: getSelects("7").length === 0,
        icon: viList,
      });
    if (isEnabled("dayNight"))
      options.push({
        value: "15",
        title: "Switch",
        disabled: getSelects("15").length === 0,
        icon: viToggleOn,
      });
    if (isEnabled("callParkings"))
      options.push({
        value: "13",
        title: "Call parking",
        disabled: getSelects("13").length === 0,
        icon: viRectangleP,
      });
    if (isEnabled("voicemails"))
      options.push({
        value: "6",
        title: "Voicemail",
        disabled: getSelects("6").length === 0,
        icon: viVoicemail,
      });
    if (isEnabled("conferences"))
      options.push({
        value: "12",
        title: "Conference",
        disabled: getSelects("12").length === 0,
        icon: viPeopleCircle,
      });
    if (isEnabled("intercoms"))
      options.push({
        value: "14",
        title: "Intercom",
        disabled: getSelects("14").length === 0,
        icon: viMegaphone,
      });
    if (isEnabled("customDestinations"))
      options.push({
        value: "11",
        title: "Custom Destination",
        disabled: getSelects("11").length === 0,
        icon: viLocationPinDot,
      });
    if (isEnabled("unrestricted")) options.push({ value: "1", title: "Unrestricted number", icon: viEarth });

    return options;
  }, [getSelects, isEnabled]);

  const activeOption = useMemo(() => {
    return getOptions().find((option) => option.value === overflowType);
  }, [getOptions, overflowType]);

  const searchResults = useSearch(getSelects(activeOption?.value) || [], searchValue, ["name", "exten"]);

  const copyValue = () => {
    navigator.clipboard
      .writeText(value?.custom || value?.exten)
      .then(() => {
        dispatch(showSnackbar("Content copied to clipboard."));
      })
      .catch(() => {
        dispatch(showSnackbar("Could not copy content to the clipboard."));
      });
  };

  const closeDrawer = () => {
    setOverflowType(null);
    setDrawer(false);
    setSearchValue("");
    setUnrestricted(null);
  };

  return (
    <Box>
      <List>
        <InputLabel
          shrink
          required={required}
          sx={{
            position: "absolute",
            top: -8,
            left: 8,
            px: 0.75,
            backgroundColor: "background.card",
            zIndex: 100,
          }}
          error={(error?.[propertyName]?.length ?? 0) >= 1}
        >
          {label}
        </InputLabel>
        <ListItem
          secondaryAction={
            value !== null && (
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  applyValue(null);
                }}
              >
                <Valkyrie icon={viXmark} />
              </IconButton>
            )
          }
          sx={{
            p: 0,
            minHeight: 30,
            "& .MuiListItemSecondaryAction-root": {
              right: "7px !important",
              "& .MuiIconButton-root": {
                borderRadius: 0.75,
              },
            },
          }}
        >
          <ListItemButton
            onClick={() => setDrawer(true)}
            sx={(theme) => ({
              py: 0.5,
              px: 1.5,
              m: 0,
              width: 1,
              border: "1px solid rgba(0,0,0,.23)",
              borderRadius: 1,
              transition: "all .2s ease-in-out",
              minHeight: 48,
              height: "auto",
              "&:hover": {
                background: "inherit",
                borderColor: "rgba(0,0,0,0.87)",
              },
              ...theme.applyStyles("dark", {
                borderColor: "rgba(255,255,255,.23)",
                "&:hover": {
                  borderColor: "#fff",
                },
              }),
              ...(error?.[propertyName] && {
                borderColor: (theme) => theme.vars.palette.error.main,
                "&:hover": {
                  borderColor: (theme) => theme.vars.palette.error.dark,
                },
              }),
            })}
          >
            {currentOverflow ? (
              <>
                <ListItemAvatar sx={{ minWidth: 32 }}>
                  <Avatar text={currentOverflow.name} px={24} icon={currentOverflow?.icon || viLocationPinDot} />
                </ListItemAvatar>
                <ListItemText
                  primary={<GoTo target={currentOverflow} hideType />}
                  secondary={<GoTo target={currentOverflow} type />}
                  sx={{
                    ".MuiTypography-root": {
                      whiteSpace: "normal",
                      overflow: "visible",
                    },
                  }}
                />
              </>
            ) : (
              <ListItemText
                primary="None"
                sx={{
                  ".MuiTypography-root": {
                    whiteSpace: "normal",
                    overflow: "visible",
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      </List>
      {error?.[propertyName] ? (
        <FormHelperText sx={{ px: 1.5 }} error={(error?.[propertyName]?.length ?? 0) >= 1}>
          {error[propertyName]}
        </FormHelperText>
      ) : helperText ? (
        <FormHelperText sx={{ px: 1.5 }}>{helperText}</FormHelperText>
      ) : null}
      <Drawer
        anchor="right"
        open={drawer}
        onClose={() => closeDrawer()}
        sx={{ zIndex: 1400 }}
        slotProps={{
          paper: {
            sx: (theme) => ({ maxWidth: 320, width: 1, p: 1.5, display: "flex", gap: 1, ...theme.scrollbar }),
            elevation: 4,
          },
        }}
      >
        {overflowType === null && (
          <>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h5">Destination</Typography>
              <IconButton onClick={() => closeDrawer()} color="inherit">
                <Valkyrie icon={viXmark} />
              </IconButton>
            </Stack>
            <Typography>Select your destination type.</Typography>
            <List>
              {currentOverflow && (
                <>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      value &&
                      !Object.hasOwn(value, "sound") &&
                      !(value && Object.hasOwn(value, "exten") && value.exten.startsWith("tt-")) && (
                        <IconButton onClick={() => copyValue()}>
                          <Valkyrie icon={viCopy} />
                        </IconButton>
                      )
                    }
                    sx={{ p: 0, minHeight: 44, m: 0 }}
                  >
                    <ListItemButton onClick={() => closeDrawer()} sx={{ minHeight: 44 }}>
                      <ListItemAvatar sx={{ minWidth: 34 }}>
                        <Avatar text={currentOverflow.name} px={24} icon={currentOverflow?.icon || viLocationPinDot} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={<GoTo target={currentOverflow} hideType />}
                        secondary={<GoTo target={currentOverflow} type />}
                        sx={{
                          ".MuiTypography-root": {
                            whiteSpace: "normal",
                            overflow: "visible",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider variant="middle" />
                </>
              )}
              {getOptions().map((_option) => (
                <ListItem disablePadding sx={{ minHeight: 44 }} key={_option.value}>
                  <ListItemButton
                    onClick={() => setOverflowType(_option.value)}
                    disabled={_option.disabled}
                    sx={{ minHeight: 44 }}
                  >
                    <ListItemIcon>
                      <Valkyrie icon={_option.icon} />
                    </ListItemIcon>
                    <ListItemText primary={_option.title} />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider variant="middle" />
              <ListItem disablePadding sx={{ minHeight: 44 }}>
                <ListItemButton onClick={() => applyValue(null)} sx={{ minHeight: 44 }}>
                  <ListItemIcon>
                    <Valkyrie icon={viTrashCan} />
                  </ListItemIcon>
                  <ListItemText primary="Remove destination" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
        {activeOption && (
          <>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={() => {
                  setOverflowType(null);
                  setSearchValue("");
                }}
                color="inherit"
              >
                <Valkyrie icon={viArrowLeft} />
              </IconButton>
              <Typography variant="h5" sx={{ m: 0 }}>
                {activeOption.title}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton onClick={() => closeDrawer()} color="inherit">
                <Valkyrie icon={viXmark} />
              </IconButton>
            </Stack>
            {activeOption.value === "1" ? (
              <>
                <InputPhone
                  fullWidth
                  label="Unrestricted number"
                  specialChars
                  id={propertyName}
                  value={unrestrictedValue}
                  onChange={(e, value) => setUnrestricted(value)}
                  error={unrestrictedValue === "0" ? { [propertyName]: ["Unrestricted number cannot be 0."] } : {}}
                  autoFocus
                />
                <Button
                  onClick={() => applyValue(unrestrictedValue)}
                  startIcon={<Valkyrie icon={viFloppyDisk} />}
                  variant="contained"
                  sx={{ my: 1 }}
                  disabled={unrestrictedValue === "0"}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
                  <Search blur value={searchValue} onChange={setSearchValue} />
                </Box>
                <List>
                  {searchResults.map((_item, key) => (
                    <ListItem disablePadding key={key} sx={{ minHeight: 44 }}>
                      <ListItemButton
                        onClick={() => applyValue(activeOption.value === "5" ? _item.id : _item.exten)}
                        sx={{ minHeight: 44 }}
                      >
                        <ListItemAvatar>
                          <Avatar text={_item.name} icon={activeOption.value !== "2" ? activeOption.icon : undefined} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={_item.name}
                          secondary={activeOption.value !== "4" ? _item.exten : undefined}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Drawer>
    </Box>
  );
}

OverflowDrawer.propTypes = {
  value: PropTypes.object,
  required: PropTypes.bool,
  propertyName: PropTypes.string,
  overflowHandler: PropTypes.func,
  options: PropTypes.object,
  filter: PropTypes.object,
  helperText: PropTypes.string,
  error: PropTypes.object,
  label: PropTypes.string,
};
